import { useEffect, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption, SeriesOption } from 'echarts';
import moment from 'moment';
import { getCssVariable } from '../../../../utils/utils';
import {
  formatFundMilionValues,
  formatLocaleNumber,
} from '../../../../helpers/number';
import { ThemeType } from '../../../../utils/type';
import {
  FundChartPeriod,
  FundChartPoint,
  FundChartsData,
} from '../../../../types/fundsummary.type';
import useProcessChartData from '../../../../hooks/useProcessChartData';
import ErrorMessage from '../../../../Components/ErrorMessage/ErrorMessage';

export interface FundChartSeries {
  nav: FundChartPoint[];
  benchmark: FundChartPoint[];
  tna: FundChartPoint[];
}

export interface FundLineChartProps {
  data?: FundChartsData;
  theme?: ThemeType;
  activePeriod: FundChartPeriod;
}

export const getFundChartColors = (theme?: ThemeType) => ({
  nav: getCssVariable('--chart-primary', theme),
  benchmark: getCssVariable('--chart-tertiary', theme),
  tna: getCssVariable('--chart-septenary', theme),
});

const toNumber = (value: number | string | null | undefined): number =>
  parseFloat(value?.toString() ?? '') || 0;

const FundLineChart = ({
  data,
  theme,
  activePeriod,
}: FundLineChartProps): JSX.Element => {
  const chartRef = useRef<ReactECharts>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance().resize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const labelColor = getCssVariable('--text-label', theme);
  const gridColor = getCssVariable('--border-primary', theme);
  const splitLineClor = getCssVariable('--border-tertiary', theme);
  const {
    nav: navColor,
    benchmark: bmColor,
    tna: tnaColor,
  } = getFundChartColors(theme);

  const { nav, benchmark, tna, baseData } = useProcessChartData(
    data?.navChart?.data,
    data?.midChart?.data,
    data?.tnaChart?.data,
  );

  const dataLength = baseData.length;

  const xAxisCategories = useMemo(() => {
    if (dataLength === 0) return [];
    const format =
      activePeriod === '3M' || activePeriod === '6M' ? 'MMM DD' : 'MMM YYYY';
    return baseData.map((item) =>
      moment(item.Date, ['YYYY/MM/DD', 'YYYYMMDD']).format(format),
    );
  }, [baseData, activePeriod, dataLength]);

  const series = useMemo<SeriesOption[]>(() => {
    if (dataLength === 0) return [];
    const result: SeriesOption[] = [];

    if (nav.length) {
      const navAllZero = nav.every((item) => !item.val);
      const navByDate = new Map(
        nav
          .filter((n) => !Number.isNaN(+n.val))
          .map((n) => [n.Date, toNumber(n.val)]),
      );
      let lastNav: number | null = null;
      result.push({
        name: 'NAV',
        type: 'line',
        yAxisIndex: 0,
        showSymbol: false,
        smooth: false,
        data: navAllZero
          ? []
          : baseData.map((base) => {
              if (navByDate.has(base.Date)) {
                lastNav = navByDate.get(base.Date) ?? lastNav;
              }
              return lastNav;
            }),
        lineStyle: { width: 1.5, color: navColor },
        itemStyle: { color: navColor },
        z: 3,
      });
    }

    if (benchmark.length) {
      // Benchmark có thể ít điểm hơn baseData -> forward-fill giá trị gần nhất
      // để đường không bị ngắt quãng.
      const bmByDate = new Map(benchmark.map((b) => [b.Date, toNumber(b.val)]));
      let lastBm: number | null = null;
      result.push({
        name: 'Benchmark',
        type: 'line',
        yAxisIndex: 0,
        showSymbol: false,
        smooth: false,
        data: baseData.map((base) => {
          if (bmByDate.has(base.Date)) {
            lastBm = bmByDate.get(base.Date) ?? lastBm;
          }
          return lastBm;
        }),
        lineStyle: { width: 1.5, color: bmColor },
        itemStyle: { color: bmColor },
        z: 3,
      });
    }

    if (tna.length) {
      const tnaByDate = new Map(tna.map((t) => [t.Date, toNumber(t.val)]));
      let lastTna: number | null = null;
      result.push({
        name: 'TNA',
        type: 'line',
        yAxisIndex: 1,
        showSymbol: false,
        smooth: false,
        data: baseData.map((base) => {
          if (tnaByDate.has(base.Date)) {
            lastTna = tnaByDate.get(base.Date) ?? lastTna;
          }
          return lastTna;
        }),

        itemStyle: { color: tnaColor },
        areaStyle: { color: tnaColor, opacity: 1 },
        emphasis: { disabled: true },
        z: 1,
      });
    }

    return result;
  }, [dataLength, nav, benchmark, tna, baseData, navColor, bmColor, tnaColor]);

  if (dataLength === 0) {
    return <ErrorMessage msg={{ label: 'Chart is currently available' }} />;
  }

  const option: EChartsOption = {
    textStyle: {
      fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
    },
    grid: {
      left: 25,
      right: 25,
      top: 12,
      bottom: 8,
      containLabel: true,
      show: true,
      borderColor: gridColor,
      borderWidth: 1,
    },
    tooltip: {
      trigger: 'axis',
      extraCssText: 'padding: 6px; min-width: 140px;',
      textStyle: { fontSize: 12 },
      axisPointer: {
        type: 'cross',
        crossStyle: { color: gridColor },
        label: { show: false },
      },
      formatter: (params: any) => {
        if (!params?.length) return '';
        const title = params[0].axisValue;
        const rows = params
          .map((p: any) => {
            const hasValue =
              p.value !== null &&
              p.value !== undefined &&
              !Number.isNaN(Number(p.value));
            const value = !hasValue
              ? '-'
              : p.seriesName === 'TNA'
                ? `${formatFundMilionValues(p.value, 0)}M`
                : `${formatLocaleNumber(Number(p.value), 2)}%`;
            return `<div style="display:flex;justify-content:space-between;gap:12px;">
              <span>${p.marker}${p.seriesName}</span><strong>${value}</strong>
            </div>`;
          })
          .join('');
        return `<div style="min-width:140px;"><div>${title}</div>${rows}</div>`;
      },
    },
    xAxis: {
      type: 'category',
      data: xAxisCategories,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        color: labelColor,
        fontSize: 11,
        align: 'center',
        showMinLabel: true,
        showMaxLabel: true,
      },
      splitLine: {
        show: true,
        lineStyle: { color: splitLineClor, opacity: 1, type: 'dashed' },
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'NAV',
        position: 'left',
        scale: true,
        nameTextStyle: { color: labelColor, fontSize: 11 },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          lineStyle: { color: splitLineClor, opacity: 1, type: 'dashed' },
        },
        axisLabel: {
          color: labelColor,
          fontSize: 11,
          formatter: (v: number) => formatLocaleNumber(v, 2),
        },
      },
      {
        type: 'value',
        name: 'TNA',
        position: 'right',
        scale: true,
        nameTextStyle: { color: labelColor, fontSize: 11 },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          color: labelColor,
          fontSize: 11,
          formatter: (val: any) => `${formatFundMilionValues(val, 0)}M`,
        },
      },
    ],
    series,
  };

  return (
    <div className="fund-data-chart">
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ width: '100%', height: '240px', maxWidth: '100%' }}
      />
    </div>
  );
};

export default FundLineChart;
